import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { Plus, Warning, CheckCircle, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

export default function DefectsPage() {
  const [defects, setDefects] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    loadDefects();
  }, []);

  const loadDefects = async () => {
    try {
      const response = await api.get('/defects');
      setDefects(response.data);
    } catch (error) {
      toast.error('Failed to load defects');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.post('/defects', {
        defect_type: formData.get('defect_type'),
        severity: formData.get('severity'),
        description: formData.get('description'),
        assigned_to: formData.get('assigned_to') || null,
      });
      toast.success('Defect reported');
      setShowCreateModal(false);
      loadDefects();
    } catch (error) {
      toast.error('Failed to create defect');
    }
  };

  const updateStatus = async (id, status, resolution = null) => {
    try {
      const params = new URLSearchParams({ status });
      if (resolution) params.append('resolution', resolution);
      await api.patch(`/defects/${id}/status?${params}`);
      toast.success('Status updated');
      loadDefects();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteDefect = async (defectId) => {
    if (!window.confirm('Are you sure you want to delete this defect? This action cannot be undone.')) {
      return;
    }
    try {
      await api.delete(`/defects/${defectId}`);
      toast.success('Defect deleted successfully');
      loadDefects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete defect');
    }
  };

  return (
    <div data-testid="defects-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">Defect Management</h1>
          <p className="mt-1 text-sm text-gray-600">Track and resolve quality defects</p>
        </div>
        <button
          data-testid="create-defect-button"
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2 rounded-sm hover:bg-gray-800 transition-colors"
        >
          <Plus size={20} weight="bold" />
          Report Defect
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading defects...</div>
      ) : defects.length === 0 ? (
        <div className="text-center py-12 bg-white border border-gray-200 rounded-sm">
          <p className="text-gray-500">No defects reported yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {defects.map((defect, idx) => (
            <div
              key={defect.id}
              data-testid={`defect-card-${idx}`}
              className="bg-white border border-gray-200 rounded-sm p-6"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <Warning size={24} weight="fill" className={`${
                      defect.severity === 'critical' ? 'text-red-600' :
                      defect.severity === 'major' ? 'text-orange-600' :
                      'text-yellow-600'
                    }`} />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{defect.defect_type}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-2 py-1 text-xs font-medium rounded-sm ${
                          defect.severity === 'critical' ? 'bg-red-100 text-red-700' :
                          defect.severity === 'major' ? 'bg-orange-100 text-orange-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {defect.severity.toUpperCase()}
                        </span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-sm ${
                          defect.status === 'closed' || defect.status === 'resolved' ? 'bg-green-100 text-green-700' :
                          defect.status === 'in-progress' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {defect.status.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-gray-600">{defect.description}</p>
                  {defect.resolution && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-sm">
                      <p className="text-xs font-medium text-green-900 mb-1">RESOLUTION</p>
                      <p className="text-sm text-green-800">{defect.resolution}</p>
                    </div>
                  )}
                  <p className="mt-3 text-xs text-gray-500">Reported on {new Date(defect.created_at).toLocaleDateString()}</p>
                </div>
                {defect.status === 'open' && (
                  <div className="flex gap-2">
                    <button
                      data-testid={`resolve-defect-${idx}`}
                      onClick={() => {
                        const resolution = prompt('Enter resolution details:');
                        if (resolution) updateStatus(defect.id, 'resolved', resolution);
                      }}
                      className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-sm hover:bg-green-700 transition-colors"
                    >
                      Resolve
                    </button>
                    {(user?.role === 'admin' || user?.role === 'qa_manager') && (
                      <button
                        data-testid={`delete-defect-${idx}`}
                        onClick={() => handleDeleteDefect(defect.id)}
                        className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-sm hover:bg-red-700 transition-colors"
                      >
                        <Trash size={16} />
                      </button>
                    )}
                  </div>
                )}
                {defect.status === 'resolved' && (
                  <button
                    onClick={() => updateStatus(defect.id, 'closed')}
                    className="px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-sm hover:bg-gray-700 transition-colors"
                  >
                    Close
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-sm w-full max-w-md">
            <div className="border-b border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900">Report Defect</h2>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">DEFECT TYPE</label>
                <input
                  name="defect_type"
                  data-testid="defect-type-input"
                  type="text"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">SEVERITY</label>
                <select
                  name="severity"
                  data-testid="defect-severity-select"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                >
                  <option value="critical">Critical</option>
                  <option value="major">Major</option>
                  <option value="minor">Minor</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">DESCRIPTION</label>
                <textarea
                  name="description"
                  data-testid="defect-description-input"
                  rows={4}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">ASSIGN TO (Optional)</label>
                <input
                  name="assigned_to"
                  data-testid="defect-assigned-input"
                  type="text"
                  placeholder="User ID"
                  className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-sm hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="defect-submit"
                  className="flex-1 px-4 py-2 bg-gray-900 text-white rounded-sm hover:bg-gray-800 transition-colors"
                >
                  Report Defect
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
